import React, {useMemo, useState} from "react";
import {Combination, MenuItemCombination} from "../../@types/stores/menu";
import {useTranslation} from "react-i18next";
import "./style.scss";
import {
    addToLog,
    getAllergenById,
    getCurrency,
    getHost,
    getPreferenceById
} from "../../helpers/ApiHelper";
import { ReactComponent as IconCheck } from "../../assets/icons/check.svg";
import menuStore from "../../stores/menu";
import {useNavigate, useParams} from "react-router";
import {moveToTop} from "../../helpers/DomHelper";
import orderStore from "../../stores/order";
import {v4 as guid} from 'uuid';
import defaultImg from "../../assets/images/default.webp";
import useLayoutStyles from "../../hooks/styles";


export interface CombinationStepProps {
    step: Combination;
    item: MenuItemCombination;
    menuId: number;
    subMenuId: number;
    isPackage?: boolean;
}

const CombinationStep = ({step, item, menuId, subMenuId, isPackage}: CombinationStepProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const styles = useLayoutStyles();

    const [articleId, setArticleId] = useState<number>(-1);

    const changeArticle = (id: number, min: number, max: number) => {
        // in a package all articles are always selected and can not be unselected
        if (isPackage) return;

        setArticleId(id);
        goNext(id);
    }

    const hasImage = useMemo(() => {
        for (const ca of step.articles) {
            if (ca.article.previewList)
                return true;
        }
        return false;
    }, [step]);

    const goNext = (articleId: number = -1) => {
        const isChange = params.change === "change";
        if (articleId !== -1) {
            const article = step.articles.find(x => x.article.id === articleId);
            if (article) {
                addToLog(`Click on combination article ${article.article.number} / ${article.article.name}, step #${step.id} / ${step.name}`);

                orderStore.addArticleToCurrentCombinationOrderLine(item, step, {
                    id: guid(),
                    articleId: article.article.id,
                    combinationId: item.id,
                    menuId,
                    subMenuId,
                    name: article.article.name,
                    number: article.article.number,
                    count: step.price ? 1 : article.quantity,
                    price: step.price || article.article.price,
                    total: step.price || article.article.price * article.quantity,
                    img: article?.article.previewList,
                    components: article.article.components
                },
                isChange ? orderStore.currentCombinationOrderLine?.steps.find(s => s.id === step.id)?.articles?.at(0)?.number : undefined);

                if (article.article.recommendedGroups)
                    menuStore.addCombinationRecommendationGroupIds(article.article.recommendedGroups);
            }

            if (article?.article.modifierGroups?.length) {
                moveToTop();
                navigate(`/menu/combination/${menuId}/${subMenuId}/${item.id}/step/${step.id}/article/${articleId}/modifiers${isChange ? "/change" : ""}`);
                return;
            }
            else if (article?.article.addonGroups?.length) {
                moveToTop();
                navigate(`/menu/combination/${menuId}/${subMenuId}/${item.id}/step/${step.id}/article/${articleId}/addons${isChange ? "/change" : ""}`);
                return;
            }
            else if (article?.article.ingredientGroups?.length) {
                moveToTop();
                navigate(`/menu/combination/${menuId}/${subMenuId}/${item.id}/step/${step.id}/article/${articleId}/ingredients${isChange ? "/change" : ""}`);
                return;
            }
        }
        else {
            orderStore.addArticleToCurrentCombinationOrderLine(item, step, {
                    id: guid(),
                    articleId: -1,
                    combinationId: item.id,
                    menuId,
                    subMenuId,
                    name: "",
                    number: guid(),
                    count: 0,
                    price: 0,
                    total: 0
                },
                isChange ? orderStore.currentCombinationOrderLine?.steps.find(s => s.id === step.id)?.articles?.at(0)?.number : undefined);
        }

        const curStepIndex = item.combination.findIndex(x => x.id === step.id);
        const nextStepIndex = item.combination.length - 1 > curStepIndex ? curStepIndex + 1 : -1;
        if (!isChange && nextStepIndex > -1)
            navigate(`/menu/combination/${menuId}/${subMenuId}/${item.id}/step/${item.combination[nextStepIndex].id}`);
        else
            navigate(`/menu/combination/info`);
    }

    return <div className="CombinationStep">
        <div className="text">
            {step.name}
            <div className="min-max">
                {step.min
                    ? <div>{t("min")}: {step.min}</div>
                    : null
                }
            </div>

            <div>
                <div className="blocks">
                    {step.articles.map((ca, i) =>
                        <div key={`combinationStep_${step.id}_article_${ca.article.id}`}
                             className={`block ${isPackage || articleId === ca.article.id ? "active" : ""} ${isPackage ? "package" : ""}`}
                             onClick={() => changeArticle(ca.article.id, step.min || 0, step.max || 100)}>
                            {isPackage || articleId === ca.article.id
                                ? <IconCheck className="check"/>
                                : null
                            }
                            {hasImage
                                ? <>
                                    <div className={`img ${ca.article.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).length ? "blur" : ""}`}>
                                        <img alt={ca.article.name} src={ca.article.previewList ? `${getHost()}${ca.article.previewList}` : (styles?.article.defaultListImage ? `${getHost()}${styles.article.defaultListImage}` : defaultImg)}/>
                                    </div>
                                    <div className="tagsImages">
                                        {ca.article.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y)?.image ?
                                            <img key={`allergenTagImage_${y}`}
                                                 src={`${getHost()}${getAllergenById(y)?.image || ""}`}
                                                 alt={getAllergenById(y)?.name}/> : null)}
                                        {ca.article.preferences?.filter(y => menuStore.filter.preferences.length && menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y)?.image ?
                                            <img key={`preferenceTagImage_${y}`}
                                                 src={`${getHost()}${getPreferenceById(y)?.image || ""}`}
                                                 alt={getPreferenceById(y)?.name}/> : null)}
                                    </div>
                                </>
                                : null
                            }
                            <div className="text">
                                <div x-data-groupId={`combinationStep_${step.id}`} className={`tagContainer ${!hasImage ? "short" : ""}`}>
                                    <div className="tags">
                                        {ca.article.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y) ?
                                            <div key={`allergenTag_${y}`}
                                                 className="tag allergen">{getAllergenById(y)?.name || ""}</div> : null)}
                                        {ca.article.preferences?.filter(y => menuStore.filter.preferences.length && menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y) ?
                                            <div key={`preferenceTag_${y}`}
                                                 className="tag preference">{getPreferenceById(y)?.name || ""}</div> : null)}
                                    </div>
                                </div>
                                <div className="title">
                                    {ca.article.name}
                                </div>
                                <div className="price">
                                    {!isPackage && !item.price && (step.price || ca.article.price)
                                        ? step.price
                                            ? `+ ${step.price.toFixed(2)} ${getCurrency()}`
                                            : `+ ${ca.article.price.toFixed(2)} ${getCurrency()}`
                                        : null
                                    }
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>

        {!isPackage && !step.min
            ? <div className="buttons">
                <div className={`button dark`} onClick={() => goNext()}>{t(`next`)}</div>
            </div>
            : null
        }
    </div>
}

export default CombinationStep;
